import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { User as UserIcon, Menu, X } from 'lucide-react';
import { isWebAppReady } from '../config/systemConfig';

export const Layout = ({ children }: { children: React.ReactNode }) => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleNavClick = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-black font-sans">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-xl md:text-2xl font-serif font-bold tracking-tighter">
            <img src="/assets/logo.png" alt="Atelier Persopol Logo" className="w-8 h-8 rounded-md" />
            <span className="hidden sm:inline">Atelier Persopol</span>
            <span className="sm:hidden">Atelier</span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {!user && (
              <>
                <Link to="/" className="hover:text-gray-600 transition-colors">Home</Link>
                <Link to="/styles" className="hover:text-gray-600 transition-colors">Styles</Link>
                <Link to="/contact-us" className="hover:text-gray-600 transition-colors">Contact Us</Link>
                <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy</Link>
              </>
            )}
          </nav>

          <div className="flex items-center space-x-4">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Desktop User Actions */}
            {user ? (
              <div className="hidden md:flex items-center space-x-4">
                <Link to="/account" className="hover:text-gray-600">
                  <UserIcon className="w-5 h-5" />
                </Link>
                <button onClick={logout} className="text-sm hover:text-gray-600">
                  Sign Out
                </button>
              </div>
            ) : (
              isWebAppReady && (
                <Link to="/signin" className="hidden md:block text-sm font-medium hover:text-gray-600">
                  Sign up / Sign in
                </Link>
              )
            )}
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <>
            <div 
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className={`fixed top-16 left-0 right-0 bg-white border-b border-gray-100 z-50 md:hidden transform transition-transform duration-300 ${
              mobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
            }`}>
              <nav className="container mx-auto px-4 py-4 space-y-4">
                {!user && (
                  <>
                    <button
                      onClick={() => handleNavClick('/')}
                      className="block w-full text-left py-3 text-lg hover:text-gray-600 transition-colors"
                    >
                      Home
                    </button>
                    <button
                      onClick={() => handleNavClick('/styles')}
                      className="block w-full text-left py-3 text-lg hover:text-gray-600 transition-colors"
                    >
                      Styles
                    </button>
                    <button
                      onClick={() => handleNavClick('/contact-us')}
                      className="block w-full text-left py-3 text-lg hover:text-gray-600 transition-colors"
                    >
                      Contact Us
                    </button>
                    <button
                      onClick={() => handleNavClick('/privacy')}
                      className="block w-full text-left py-3 text-lg hover:text-gray-600 transition-colors"
                    >
                      Privacy
                    </button>
                  </>
                )}
                {user ? (
                  <>
                    <button
                      onClick={() => handleNavClick('/account')}
                      className="flex items-center gap-3 w-full text-left py-3 text-lg hover:text-gray-600 transition-colors"
                    >
                      <UserIcon className="w-5 h-5" />
                      Account
                    </button>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left py-3 text-lg hover:text-gray-600 transition-colors"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  isWebAppReady && (
                    <button
                      onClick={() => handleNavClick('/signin')}
                      className="block w-full text-left py-3 text-lg font-medium hover:text-gray-600 transition-colors"
                    >
                      Sign up / Sign in
                    </button>
                  )
                )}
              </nav>
            </div>
          </>
        )}
      </header>

      <main className="flex-grow">
        {children}
      </main>

      <footer className="bg-black text-white py-12">
        <div className="container mx-auto px-4 grid md:grid-cols-4 gap-8">
          <div>
            <h3 className="font-serif text-xl mb-4">Atelier Persopol</h3>
            <p className="text-gray-400 text-sm">Where your moments become living design</p>
          </div>
          
          <div>
            <h3 className="font-serif text-xl mb-4">Support</h3>
            <div className="flex flex-col space-y-2 text-sm text-gray-400">
              <Link to="/contact-us" className="hover:text-white transition-colors">Contact Us</Link>
            </div>
          </div>
          
          <div>
            <h3 className="font-serif text-xl mb-4">Legal</h3>
            <div className="flex flex-col space-y-2 text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};


